import * as PopoverPrimitive from 'radix-ui/popover';
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react';
import { useRef } from 'react';
import { cx } from '../cx';
import { isWithinNestedLayer, nestedLayerAttributes } from '../nestedLayer';
import { MENU_SURFACE, MENU_VIEWPORT } from '../SelectMenu/SelectMenu';

/**
 * THE FLOATING EDITOR OF A DATA CELL (polish wave 1).
 *
 * A table cell used to mount the record page's FORM control inside its own `<td>`, and a
 * form control is not the shape of a cell: a multi-select rendered a whole fieldset with a
 * legend and one checkbox per option INSIDE the row, so the row grew to the height of the
 * option list and every neighbouring row moved. The editor is a layer now - portalled,
 * anchored to the cell, and the row is exactly as tall while it is open as it was before.
 *
 * IT IS THE SAME POPPER AS EVERY OTHER FLOATING PANEL, deliberately: `MENU_SURFACE` plus
 * `MENU_VIEWPORT` means an editor near the bottom of the screen flips above the cell and
 * an editor with more options than there is room for scrolls inside itself, without this
 * component knowing either rule. A second collision implementation is how two panels in
 * one product start behaving differently at the edge of the screen.
 *
 * BELOW THE CELL, NOT OVER IT. The cell keeps rendering its value and wears the focus ring
 * that says which cell is being edited (`.data-cell-editing`), which an editor painted on
 * top of it would hide - and hiding it would put us back where the row tint was.
 *
 * WIDTH: at least the cell's own, so an editor is never narrower than the value it
 * replaces, and never wider than the screen. Radix publishes the anchor's width as
 * `--radix-popper-anchor-width`; the cap is the popover's own rule.
 *
 * AN OVERLAY OPENED FROM INSIDE THE PANEL DOES NOT END THE EDIT (wave 1c,
 * `file-attachments.md` finding 2). A confirmation portals to the body, so it is outside this
 * panel by every test there is - Radix's own dismissal, and the caller's `onBlur` and
 * `onKeyDown`, which React delivers from a portal THROUGH THE REACT TREE and therefore
 * through this panel. All three closed the editor and unmounted the confirmation with it
 * before it painted (measured: zero dialogs on the page 1.5s after the press). An overlay
 * that declares itself nested is exempted from all three (`ui/nestedLayer.ts`); every other
 * pointer, blur and key is answered exactly as before.
 */
export interface CellEditorProps {
  readonly open: boolean;
  /** Radix closes on Escape, on a pointer outside, and when focus leaves. */
  readonly onOpenChange: (open: boolean) => void;
  /** Fired before the close when the close WAS an Escape - the caller's cancel signal. */
  readonly onEscape?: (() => void) | undefined;
  /** The cell. Rendered exactly where the caller put it; the panel hangs off it. */
  readonly anchor: ReactNode;
  /** The panel's accessible name. A sentence: it is read out loud on open. */
  readonly label: string;
  /**
   * On the PANEL, not on a wrapper inside it. A key or a focus event that a caller cares
   * about is one that concerns the whole editor - Enter commits it, focus leaving it ends
   * it - and the panel is the element that owns both. It is also the element with a role:
   * a `div` in here carrying the same handlers is a static element with interactions.
   */
  readonly onKeyDown?: ((event: KeyboardEvent<HTMLDivElement>) => void) | undefined;
  readonly onBlur?: ((event: FocusEvent<HTMLDivElement>) => void) | undefined;
  /**
   * THIS EDITOR OPENS FROM INSIDE ANOTHER DISMISSABLE LAYER, and interactions in it must
   * not read as "outside" that one (`ui/nestedLayer.ts`). The filter row's value editor is
   * the first: it hangs off a control inside the filter popover, which would otherwise
   * dismiss the moment somebody ticked an option in it. Opt-in for the reason `Dialog`'s
   * flag is - a menu that opens an editor SHOULD close.
   */
  readonly nested?: boolean | undefined;
  readonly children: ReactNode;
  /**
   * NO `className` (`ui-foundation.md` D1). A floating or chrome primitive owns its width,
   * its padding, its max-height and its collision behaviour; a caller that reaches into any
   * of them is the caller who eventually gets one of them wrong. Placement belongs to the
   * ROW the control sits in, so wrap it in a box and give the box the width.
   */
}

/** What counts as "the editor" for the opening focus. Order is DOM order. */
const FIRST_CONTROL = 'input, textarea, select, button, [tabindex]:not([tabindex="-1"])';

const PANEL =
  'min-w-(--radix-popper-anchor-width) max-w-[min(22rem,calc(100vw-2*var(--gutter)))] p-2';

export function CellEditor({
  open,
  onOpenChange,
  onEscape,
  anchor,
  label,
  onKeyDown,
  onBlur,
  nested = false,
  children,
}: CellEditorProps) {
  const panel = useRef<HTMLDivElement>(null);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Anchor asChild>{anchor}</PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          {...nestedLayerAttributes(nested)}
          ref={panel}
          aria-label={label}
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          {...(onEscape !== undefined ? { onEscapeKeyDown: onEscape } : {})}
          onInteractOutside={(event) => {
            if (isWithinNestedLayer(event.detail.originalEvent.target)) event.preventDefault();
          }}
          onKeyDown={(event) => {
            if (isWithinNestedLayer(event.target)) return;
            onKeyDown?.(event);
          }}
          onBlur={(event) => {
            // BOTH ENDS: focus moving INTO a nested overlay, and focus moving between two
            // controls inside one - the second is a focusout whose target is already there.
            if (isWithinNestedLayer(event.target) || isWithinNestedLayer(event.relatedTarget))
              return;
            onBlur?.(event);
          }}
          onOpenAutoFocus={(event) => {
            // The mouse path and the keyboard path end in the same place: a focused
            // control. Radix would focus the PANEL, which is one Tab away from useful.
            event.preventDefault();
            panel.current?.querySelector<HTMLElement>(FIRST_CONTROL)?.focus();
          }}
          onCloseAutoFocus={(event) => {
            /**
             * Radix would focus its Trigger, and there is none: the anchor is a table
             * cell, so `triggerRef` is the same null ref that dropped focus onto <body>
             * from every dialog in the product before the focus-return round
             * (`docs/ios26-mechanisms.md`). Where focus goes after an edit depends on how
             * the edit ENDED - Enter and Escape return to the cell, tabbing away must not
             * be dragged back - so the cell decides it, not this component.
             */
            event.preventDefault();
          }}
          className={cx(MENU_SURFACE, MENU_VIEWPORT, PANEL)}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
